/**
 * Selectable chip.
 *
 * Activities, travel modes and budget levels are all "pick some of these"
 * controls; a chip renders the same semantics with a visible pressed state and
 * keyboard support, without pulling in a listbox implementation.
 */

import type { ComponentProps } from 'react'

import { cn } from '../../lib/format'

export interface ChipProps extends Omit<ComponentProps<'button'>, 'onSelect'> {
  selected: boolean
}

export function Chip({ selected, className, type, ...props }: ChipProps) {
  return (
    <button
      type={type ?? 'button'}
      role="checkbox"
      aria-checked={selected}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
        selected
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-background text-foreground hover:bg-accent',
        className,
      )}
      {...props}
    />
  )
}

export interface ChipGroupProps extends ComponentProps<'div'> {
  label: string
}

/** Accessible group wrapper for a set of chips. */
export function ChipGroup({ label, className, ...props }: ChipGroupProps) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn('flex flex-wrap gap-2', className)}
      {...props}
    />
  )
}
